import createAsyncGetters, {
  AsyncGetterAPI,
} from '../config/asyncGetters/createAsyncGetters';
import { getLocale } from '../getters/user';
import { AvatarFrameId } from '../profile/frames';
import { getPlayerProfileFromLocale } from '../profile/profile.getters';
import {
  getBuruFriends,
  getUnfriendedFriends,
  getUsersWithPendingRequest,
} from './friends.getters';
import { PlayerEntryData } from './friends.ruleset';
import { isOnlineForVisit } from '../visit/visit.presence';
import { nonFriendsSearch } from '../visit/visit.asyncGetters';
import { getTeamNamesAndIcons } from './friends.asyncGetters';
import { createReportableError } from '../utils/errors';

/** A single player row returned by `api.searchPlayers`. */
type PlayerSuggestionResult = Awaited<
  ReturnType<AsyncGetterAPI['searchPlayers']>
>['results'][number];

/** A single player row returned by Visit's `nonFriendsSearch` (leaner fields). */
type NonFriendsSearchResult = Awaited<
  ReturnType<typeof nonFriendsSearch>
>['results'][number];

export const friendSuggestionsAsyncGetters = createAsyncGetters({
  /**
   * Fetch friend suggestions sourced from the user's social cluster
   * (friends-of-friends), with a cold-stranger fallback.
   *
   * The cluster query finds public players who already list one of the user's
   * friends as their own friend (shared social graph). When the cluster yields
   * fewer than 5 candidates — or the cluster query fails — we top up the list
   * with a generic public-player fallback (same exclusions as
   * `searchNonFriendPlayerEntries`, minus the name-search clause).
   *
   * Cluster entries are tagged `originSubFeature: 'friend_list_cluster'`,
   * fallback entries `'friend_list_suggestions'`. The final list is
   * cluster-first, de-duplicated, and truncated to 5.
   */
  fetchClusterFriendSuggestions: async (
    _: {},
    api,
  ): Promise<{ suggestions: PlayerEntryData[] }> => {
    // Number of suggestions returned to the caller.
    const count = 5;
    // Cap on how many of the user's friends seed the cluster query, to bound
    // the `containsAnyOf` clause for users with very large friend lists.
    const clusterFriendCap = 100;
    // Over-fetch candidates per query so we have dedupe/exclusion headroom
    // before truncating the merged list to `count`.
    const candidateLimit = 20;
    const ownState = await api.getOwnState();
    const locale = getLocale(ownState);
    const friends = getBuruFriends(ownState);
    const pendingRequestUserIds = getUsersWithPendingRequest(ownState);
    const unfriendedFriends = getUnfriendedFriends(ownState);

    const excludeIds = [
      ownState.id,
      ...friends,
      ...pendingRequestUserIds,
      ...unfriendedFriends,
      // Players the user themselves unfriended live only in friendsBlacklist
      // (removeFriend deletes the buruFriends entry), so getUnfriendedFriends
      // misses them — exclude the blacklist explicitly.
      ...ownState.friendsBlacklist,
    ];

    // Player fields selected by `searchNonFriendPlayerEntries`, the set needed
    // to build a `PlayerEntryData` for each suggestion.
    const suggestionFields = [
      'updatedAt',
      'profileName',
      'profilePhoto',
      'publicProfileName',
      'publicProfilePhoto',
      'profileAvatarFrame',
      'match2Level',
      'petSpecies',
      'petName',
      'petState',
      'presence',
      'petIsInDaycare',
      'currentTeamId',
    ] as const;

    // CLUSTER: public players who share a friend with the user. Skip entirely
    // when the user has no friends, and degrade gracefully on query failure.
    let clusterResults: PlayerSuggestionResult[] = [];
    if (friends.length > 0) {
      try {
        const clusterSearch = await api.searchPlayers({
          where: {
            friendIds: {
              containsAnyOf: friends.slice(0, clusterFriendCap),
            },
            id: {
              isNotOneOf: excludeIds,
            },
            hasPublicProfile: {
              is: true,
            },
            petSpecies: {
              isNoneOf: [''],
            },
          },
          sort: [
            {
              field: 'updatedAt',
              order: 'desc',
            },
          ],
          fields: [...suggestionFields],
          limit: candidateLimit,
        });
        clusterResults = clusterSearch.results;
      } catch (error) {
        // Report, then treat cluster failures as an empty cluster and rely on
        // the fallback.
        api.reportError(createReportableError('error', error as Error));
        clusterResults = [];
      }
    }

    // FALLBACK: cold-stranger top-up sourced from Visit's `nonFriendsSearch` —
    // the same logic Visit suggestions use (per spec). Only queried when the
    // cluster did not yield enough candidates (also covers the throw case).
    // `nonFriendsSearch` only excludes self + friends in-query; the merge below
    // drops anything else in `excludeIds` (pending / unfriended / blacklist).
    let fallbackResults: NonFriendsSearchResult[] = [];
    if (clusterResults.length < count) {
      try {
        const fallbackSearch = await nonFriendsSearch(ownState, api);
        fallbackResults = fallbackSearch.results;
      } catch (error) {
        // Report, then degrade to no fallback if the cold-stranger search
        // fails.
        api.reportError(createReportableError('error', error as Error));
        fallbackResults = [];
      }
    }

    // Team profiles only exist on cluster rows; `nonFriendsSearch` does not
    // fetch currentTeamId.
    const teamIds = clusterResults.flatMap((p) =>
      p.currentTeamId ? [p.currentTeamId] : [],
    );
    const teamProfiles = await getTeamNamesAndIcons(api, [...new Set(teamIds)]);

    const toClusterEntry = (data: PlayerSuggestionResult): PlayerEntryData => {
      const profile = getPlayerProfileFromLocale(
        locale,
        data.publicProfileName,
        data.publicProfilePhoto,
        data.profileName,
        data.profilePhoto,
      );
      const teamProfile = data.currentTeamId
        ? teamProfiles[data.currentTeamId]
        : undefined;

      return {
        updatedAt: data.updatedAt,
        id: data.id,
        name: profile.name,
        photo: profile.photo,
        level: data.match2Level,
        avatarFrame: data.profileAvatarFrame as AvatarFrameId,
        petSpecies: data.petSpecies || 'ball',
        petName: data.petName,
        petState: data.petState,
        isFriend: false,
        isOnline: isOnlineForVisit(data.presence),
        canVisit: !isOnlineForVisit(data.presence) && !data.petIsInDaycare,
        teamName: teamProfile?.name,
        teamIcon: teamProfile?.icon,
        originSubFeature: 'friend_list_cluster',
      };
    };

    // `nonFriendsSearch` returns a leaner field set than the cluster query, so
    // the fields the suggestion tile doesn't render (level / petName / team /
    // visit availability) fall back to safe defaults.
    const toFallbackEntry = (data: NonFriendsSearchResult): PlayerEntryData => {
      const profile = getPlayerProfileFromLocale(
        locale,
        data.publicProfileName,
        data.publicProfilePhoto,
      );

      return {
        updatedAt: data.updatedAt,
        id: data.id,
        name: profile.name,
        photo: profile.photo,
        level: 0,
        avatarFrame: data.profileAvatarFrame as AvatarFrameId,
        petSpecies: data.petSpecies || 'ball',
        petName: '',
        petState: data.petState ?? '',
        isFriend: false,
        isOnline: false,
        canVisit: false,
        originSubFeature: 'friend_list_suggestions',
      };
    };

    // MERGE: cluster first, then fallback entries not already present and not
    // excluded, truncated to `count`.
    const clusterEntries = clusterResults.map(toClusterEntry);
    const seenIds = new Set([
      ...excludeIds,
      ...clusterEntries.map((e) => e.id),
    ]);

    const suggestions = [...clusterEntries];
    for (const data of fallbackResults) {
      if (suggestions.length >= count) break;
      if (seenIds.has(data.id)) continue;
      seenIds.add(data.id);
      suggestions.push(toFallbackEntry(data));
    }
    suggestions.length = Math.min(suggestions.length, count);

    return { suggestions };
  },
});
